import { useCallback } from "react";
import { useUserStore } from "@/stores/useUserStore.ts";
import { SigROn } from "@/types/signalr-functions.ts";
import type { FlowGenBasic, FlowGeneratorSessionIntervention } from "@/types/flow.ts";
import { useSignalROn } from "@/helpers/signalr.ts";
import { useChatStore } from "@/stores/useChatStore.ts";
import type { SocratesSessionStatus } from "@/types/socrates.ts";
import { JsonParse } from "@/helpers/json.ts";
import { useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";
import { toastAndWarn } from "@/helpers/toast.ts";

export const useHookGlobal = () => {
  const { AddNewSession, SetSessionCompleted } = useAssignedChatStore();
  const { SetFlowGeneratorSessionIntervention, SetFlowGeneratorSession, SetSocratesSession } =
    useChatStore();

  const dispatchFlowGenCallback = useCallback(
    (updatedSession: Partial<FlowGenBasic>) => {
      const { userId, tutorId } = useUserStore.getState();

      if (!updatedSession.flowGeneratorSessionId) return;

      const { assignedSessions } = useAssignedChatStore.getState();

      // Check that this flowGen is assigned to us, if it's new, add it into the queue
      if (updatedSession.tutorId === tutorId) {
        if (!assignedSessions.get(updatedSession.flowGeneratorSessionId)) {
          // Note: Giving it basic info that'll get filled in by an api call later
          AddNewSession({
            assignedChat: {
              flowGeneratorSessionId: updatedSession.flowGeneratorSessionId,
              flowGeneratorSessionInterventionId: 0,
              dateTutorJoined: new Date(0),
              tutorId: tutorId,
              tutorUserId: userId,
            },
          });
        }
      }

      // Sanity check its it's not already on our list, and we need to mark it as complete
      if (updatedSession.tutorId === null || updatedSession.tutorId !== tutorId) {
        const session = assignedSessions.get(updatedSession.flowGeneratorSessionId);

        if (session) {
          SetSessionCompleted(updatedSession.flowGeneratorSessionId);
        }
      }

      const { chats } = useChatStore.getState();

      const updatedChat = chats.get(updatedSession.flowGeneratorSessionId);
      // Most likely too early, we can't use this as it's a partial update
      if (!updatedChat || !updatedChat.flowGeneratorSession) {
        console.warn("Received session update for session that does not exist");
        return;
      }

      // Manually place items for now
      // TODO: Improve api to not group updates like this, this was my own misunderstanding
      const existingFlow = updatedChat.flowGeneratorSession;

      if (existingFlow.flowGeneratorSession) {
        // shorthand as this may get ugly
        const ef = existingFlow.flowGeneratorSession;

        existingFlow.flowGeneratorSession.currentSequence =
          updatedSession.currentSequence ?? ef.currentSequence;
        existingFlow.flowGeneratorSession.dateCreated =
          updatedSession.dateCreated ?? ef.dateCreated;
        existingFlow.flowGeneratorSession.position = updatedSession.position ?? ef.position;
        existingFlow.flowGeneratorSession.templateSlug =
          updatedSession.templateSlug ?? ef.templateSlug;
      }

      existingFlow.firstName = updatedSession.firstName ?? existingFlow.firstName;
      existingFlow.lastName = updatedSession.lastName ?? existingFlow.lastName;
      existingFlow.yearGroup = updatedSession.yearGroup ?? existingFlow.yearGroup;
      existingFlow.tutorId = updatedSession.tutorId ?? existingFlow.tutorId;
      existingFlow.tutorUserId = updatedSession.tutorUserId ?? existingFlow.tutorUserId;
      existingFlow.previousTutorId = updatedSession.previousTutorId ?? existingFlow.previousTutorId;
      existingFlow.isAutoModerated = updatedSession.isAutoModerated ?? existingFlow.isAutoModerated;
      existingFlow.isAutoResponseEnabled =
        updatedSession.isAutoResponseEnabled ?? existingFlow.isAutoResponseEnabled;
      existingFlow.isProactiveIntervention =
        updatedSession.isProactiveIntervention ?? existingFlow.isProactiveIntervention;

      SetFlowGeneratorSession(updatedSession.flowGeneratorSessionId, existingFlow);
    },
    [AddNewSession, SetSessionCompleted, SetFlowGeneratorSession],
  );

  const interventionUpdateCallback = useCallback(
    (data: string) => {
      const updatedIntervention = JsonParse<FlowGeneratorSessionIntervention>(data);
      if (!updatedIntervention) {
        toastAndWarn("Could not decode intervention callback json, skipping", data);
        return;
      }

      SetFlowGeneratorSessionIntervention(
        updatedIntervention.FlowGeneratorSessionId,
        updatedIntervention,
      );
    },
    [SetFlowGeneratorSessionIntervention],
  );

  const dispatchSocStatus = useCallback(
    (data: string) => {
      const status = JsonParse<SocratesSessionStatus>(data);
      if (!status) {
        toastAndWarn("Could not decode Socrates session status json, skipping");
        return;
      }

      const { chats } = useChatStore.getState();
      const foundChat = chats.get(status.flowGeneratorSessionId);

      if (!foundChat) return;
      const session = foundChat.socrates.session;
      if (session) {
        session.status = status.status;
        SetSocratesSession(status.flowGeneratorSessionId, session);
      } else {
        SetSocratesSession(status.flowGeneratorSessionId, {
          learnerSessionId: status.socratesSessionId,
          status: status.status,
        });
      }
    },
    [SetSocratesSession],
  );

  useSignalROn(SigROn.DispatchFlowGeneratorSession, dispatchFlowGenCallback);
  useSignalROn(SigROn.InterventionUpdated, interventionUpdateCallback);
  useSignalROn(SigROn.DispatchSocratesSessionStatus, dispatchSocStatus);

  return {};
};
